import { TempBadgePreview } from 'containers/jobsite/tabs/jobsiteTempBadges/components/TempBadgePreview';
import { JobsitePrinterOption } from 'containers/utils';
import React from 'react';

import { SelectOptionElement } from '@odin-labs/components';
import cn from 'classnames';
import { DeepMap } from 'react-hook-form';
import {
  CreateTempBadgeInput,
  FileInput,
  useGetJobsiteWithBadgeTemplatesQuery,
} from 'apollo/generated/client-operations';
import { tryFileCompression } from 'utils';
import {
  acceptImages,
  acceptPdf,
  dropzoneValidation,
} from 'components/dropzone/utils';
import {
  FormInputTypes,
  getUpdateInputValueFunction,
  GridColSpan,
  TypedFormInputs,
} from 'components/form';
import { PrintIcon } from 'components/icons';
import {
  JobsiteBadgeTemplateOption,
  getJobsiteBadgeTemplatesOptions,
} from 'containers/worker/helpers/utils';
import { CreateTempBadgeFormData } from './types';

export const useJobsiteTempBadgeTemplateOptions = (
  jobsiteId: string,
): {
  tempBadgeTemplateOptions: JobsiteBadgeTemplateOption[];
  defaultTempBadgeTemplateOption: JobsiteBadgeTemplateOption | null;
  loading: boolean;
} => {
  const { data, loading } = useGetJobsiteWithBadgeTemplatesQuery({
    fetchPolicy: 'no-cache',
    skip: !jobsiteId,
    variables: { jobsiteId },
  });

  const options = React.useMemo(() => {
    if (!data) return [];
    const jobsiteBadgeTemplates =
      data?.getJobsite.jobsiteBadgeTemplates.edges.map(({ node }) => node);
    return getJobsiteBadgeTemplatesOptions(jobsiteBadgeTemplates, true);
  }, [data]);

  return {
    tempBadgeTemplateOptions: options,
    defaultTempBadgeTemplateOption:
      options.find((opt) => opt.isDefault) ?? null,
    loading,
  };
};

export const getFormInputs = (args?: {
  badgeImageUrl?: string;
  jobsiteName: string;
  printerOptions: SelectOptionElement[];
  badgeTemplateOptions: JobsiteBadgeTemplateOption[];
}): TypedFormInputs<CreateTempBadgeFormData> => {
  const { badgeImageUrl, jobsiteName, printerOptions, badgeTemplateOptions } =
    args ?? {};
  return {
    photo: {
      element: FormInputTypes.CustomContent,
      elementProps: {
        content: badgeImageUrl && (
          <img
            src={badgeImageUrl}
            className="odin-object-cover odin-min-h-full odin-py-6"
            alt="appears on the badge"
          />
        ),
      },
      layout: cn(
        badgeImageUrl ? 'odin-col-span-3 sm:odin-col-span-1' : 'odin-hidden',
      ),
    },
    badgeImageFile: {
      element: FormInputTypes.Dropzone,
      label: 'Replace Photo',
      elementProps: {
        accept: {
          ...acceptPdf,
          ...acceptImages,
        },
      },
      validation: {
        validate: dropzoneValidation,
      },
      layout: cn(
        'odin-hidden',
        badgeImageUrl
          ? 'odin-col-span-3 sm:odin-col-span-5'
          : 'odin-col-span-6',
      ),
    },
    jobsiteBadgeTemplate: {
      element: FormInputTypes.Select,
      label: 'Template',
      layout: [GridColSpan.SpanFull, GridColSpan.SmSpan6],
      elementProps: {
        clearToNull: true,
        isClearable: false,
        disabled: !badgeTemplateOptions?.length,
        options: badgeTemplateOptions ?? [],
      },
      validation: { required: true },
    },
    badgeName: {
      element: FormInputTypes.Field,
      label: 'Name',
      validation: { required: true },
      layout: cn('odin-col-span-6'),
    },
    badgePreview: {
      element: FormInputTypes.CustomContent,
      elementProps: {
        content: (
          <div className="odin-flex odin-justify-center">
            <TempBadgePreview jobsiteName={jobsiteName} />
          </div>
        ),
      },
      layout: 'odin-col-span-full',
    },
    jobsitePrinter: {
      element: FormInputTypes.Select,
      label: 'Printer',
      layout: [GridColSpan.SpanFull, GridColSpan.SmSpan6],
      elementProps: {
        clearToNull: true,
        isClearable: false,
        disabled: !printerOptions,
        options: printerOptions ?? [],
        icon: PrintIcon,
      },
      validation: { required: true },
    },
  };
};

export const getFormDefaultValues = ({
  defaultPrinterOption,
  defaultTempBadgeTemplateOption,
}: {
  defaultPrinterOption?: JobsitePrinterOption;
  defaultTempBadgeTemplateOption?: JobsiteBadgeTemplateOption;
}): CreateTempBadgeFormData => {
  return {
    badgeName: '',
    badgeImageFile: null,
    jobsitePrinter: defaultPrinterOption ?? null,
    jobsiteBadgeTemplate: defaultTempBadgeTemplateOption ?? null,
  };
};

export const getInput = async (
  jobsiteId: string,
  data: CreateTempBadgeFormData,
  dirtyFields: DeepMap<CreateTempBadgeFormData, true>,
): Promise<CreateTempBadgeInput> => {
  const getUpdateInputValue = getUpdateInputValueFunction(data, dirtyFields);

  const getFileInput = async (): Promise<FileInput> => {
    const value = getUpdateInputValue('badgeImageFile');
    if (value === undefined) return undefined;
    if (value === null) return null;
    const fileData = await tryFileCompression(value as File);
    return { uploadData: fileData, isPublic: false };
  };

  return {
    jobsiteId,
    badgeName: data.badgeName,
    fileInput: await getFileInput(),
    jobsitePrinterId: data.jobsitePrinter.value,
    badgeTemplateId: data.jobsiteBadgeTemplate.id,
  };
};
